import styles from "./style.module.scss";

import { useAppSelector } from "@/store/hooks";
import DynamicForm, { DynamicFormRef, SELECT_INPUT_TYPE } from "@mavichpe/dynamic-forms";
import ClientStatus from "./form";
import { HiddenableContainer } from "@components/layout/HiddenableContainer";
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPrint } from "@fortawesome/free-solid-svg-icons";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import LoadingScreen from "@components/content/LoadingScreen";
import { showAlert } from "@utils/Alert";
import DocumentStatusList, { DocumentsEntryType } from "../DocumentStatusList";
import moment from "moment";
import { ClientStatusFormData } from "@/formTypes/ClientStatusFormDataType";
import { loadClientStatusData } from "@utils/DocumentStatus";
import SendDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendDocumentToEmailOffCanvas";
import { OpenSendDocumentPubSub } from "@utils/pubsubs/PubSub";
import { STATUS_FILTER } from "@utils/Constants";
import { safeEncodeBase64 } from "@utils/Utils";

type ClientStatusFormProps = {
  useToggle?: boolean,
  hideAdditionalActions?: boolean,
  preventMultipleCurrency?: boolean,
  disableProjectedDate?: boolean,
  forceClientSelect?: boolean,
  fieldTitle?: string,
  filters?: (keyof typeof STATUS_FILTER)[],
  onDocumentToogle?: (documents: DocumentsEntryType[], interestExemptedDocuments: string[]) => void,
  onShowResult?: (formData: ClientStatusFormData) => void,
  children?: ReactNode,
}

const ClientStatusForm = forwardRef<DynamicFormRef, ClientStatusFormProps>(
  (props, ref) => {
    const { useToggle = false, forceClientSelect = false, disableProjectedDate = false,
      preventMultipleCurrency = false, filters = [], hideAdditionalActions = false,
      onShowResult, onDocumentToogle, children } = props
    const [projectedData, setProjectedData] = useState<string>("");
    const [documents, setDocuments] = useState<DocumentsEntryType[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showResults, setShowResults] = useState<boolean>(false);
    const [clients, setClients] = useState<ClientDataType[]>([]);
    const [clientStatusProps, setClientStatusProps] = useState<ClientStatusFormData>({} as ClientStatusFormData);
    const suscriptionData = useAppSelector(state => state.suscription.suscription)

    const formRef = useRef<DynamicFormRef>(null);
    useImperativeHandle(ref, () => formRef.current as DynamicFormRef, []);

    useEffect(() => {
      const today = moment();
      const formData: ClientStatusFormData = {
        kind: "pendientes",
        endDate: today.format("YYYY-MM-DD")
      }
      formRef.current?.setFormValue("multiple", formData, "multipleData")
      loadClients()
    }, [])

    const loadClients = async () => {
      setIsLoading(true)
      try {
        const { items } = await ClientApi.fetchAll();
        setClients(items);
      } finally {
        setIsLoading(false)
      }
    }

    const onInputKeyDown = (name: keyof ClientStatusFormData | "client_code", event?: React.KeyboardEvent<any>) => {
      if (name === "client_code") {
        if (event?.key == "Enter") {
          event?.preventDefault();
          event?.stopPropagation();
          const input = event.target as HTMLInputElement
          selectClientByCode(input.value)
        }
      }
    }
    const selectClientByCode = async (code: string) => {
      const client = clients.find(c => c.codigo === code);
      if (client?.id) {
        formRef.current?.setFormValue("clientId", client.id, "String");
      } else {
        showAlert("danger", "No existe ningun cliente con ese codigo");
      }
    }

    const pickStatusFilter = (keys: string[]) => {
      return keys.reduce((acc, key) => {
        if (STATUS_FILTER.hasOwnProperty(key)) {
          acc[key] = STATUS_FILTER[key as keyof typeof STATUS_FILTER];
        }
        return acc;
      }, {} as Record<string, string>);
    };

    const getFormConfig = () => {
      const endDateInput = ClientStatus.sections.flatMap(s => s.fields).flat().find(f => f.name === "endDate")
      if (endDateInput) {
        if (disableProjectedDate) {
          endDateInput.type = "hidden"
        } else {
          endDateInput.type = "date"
        }
      }

      const zoneIdInput = ClientStatus.sections.flatMap(s => s.fields).flat().find(f => f.name === "zoneId")
      const clientIdInput = ClientStatus.sections.flatMap(s => s.fields).flat().find(f => f.name === "clientId")
      if (forceClientSelect) {
        if (zoneIdInput) {
          zoneIdInput.type = "hidden"
        }
        if (clientIdInput) {
          clientIdInput.optionsFunction = async () => {
            const clients = await ClientApi.optionsList();
            return {
              ...clients
            }
          };
        }
      } else {
        if (zoneIdInput) {
          zoneIdInput.type = SELECT_INPUT_TYPE
        }
        if (clientIdInput) {
          clientIdInput.optionsFunction = async () => {
            const clients = await ClientApi.optionsList();
            return {
              "-2": " Todos los clientes",
              ...clients
            }
          };
        }
      }
      const kindInput = ClientStatus.sections.flatMap(s => s.fields).flat().find(f => f.name === "kind")
      if (kindInput) {
        if (filters.length > 0) {
          const filterOptions = pickStatusFilter(filters);
          kindInput.options = filterOptions
        } else {
          kindInput.options = STATUS_FILTER
        }
      }

      return ClientStatus
    }

    const generateClientStatus = async (formData: ClientStatusFormData) => {
      setIsLoading(true)
      try {
        setProjectedData(formData.endDate)
        setClientStatusProps(formData)

        const statusData = await loadClientStatusData(formData)

        setDocuments(statusData);
        setShowResults(true)
        if (onShowResult) {
          onShowResult(formData)
        }
      } catch (err) {
        if (err instanceof Error) {
          const errorMessage = err.message || "No fue posible generar el estado de cuenta";
          showAlert("danger", errorMessage)
        }
      }
      finally {
        setIsLoading(false)
      }
    }

    const printClientStatus = () => {
      const queryProps = safeEncodeBase64(JSON.stringify(clientStatusProps))
      if (clientStatusProps) {
        window.open(`/user/clients/printStatus/${queryProps}`)
      }
    }

    const triggerEmailSend = () => {
      const { clientId } = clientStatusProps;
      const queryProps = safeEncodeBase64(JSON.stringify(clientStatusProps))
      let email = ""
      if (clientId) {
        const client = clients.find(c => c.id == clientId)
        if (client && client.email) {
          email = client.email
        }
      }

      OpenSendDocumentPubSub.publish({
        openOffCanvas: true,
        emails: email,
        sourceURL: `/user/clients/printStatus/${queryProps}`,
        emailProps: {
          emailBody: suscriptionData?.clientstatus_email || "",
          emailFilename: `Estado de Cuenta`,
          emailSubject: `Estado de Cuenta`,
          Suscription: {
            email: suscriptionData?.email || ""
          }
        }
      })
    }

    const reFetchData = () => {
      formRef.current?.triggerFormSubmit()
    }

    return (
      <>
        <DynamicForm<ClientStatusFormData>
          ref={formRef}
          id="statusForm"
          formConfig={getFormConfig()}
          onInputKeyDown={onInputKeyDown}
          onSubmit={generateClientStatus}
        >
        </DynamicForm>
        <Row>
          <Col
            xs={{
              offset: 1,
              size: 10
            }}
            sm={{
              offset: 4,
              size: 7
            }} >
            <div className={styles.buttonGroup}>
              <Button size="lg" color="primary" type="submit" form="statusForm">
                Cargar
              </Button>
              <Button hidden={!showResults || hideAdditionalActions} size="lg" color="success" type="button" onClick={printClientStatus}>
                <FontAwesomeIcon className={styles.buttonIcon} icon={faPrint} />
                Imprimir
              </Button>
              <Button hidden={!showResults || hideAdditionalActions} size="lg" color="success" type="button" onClick={triggerEmailSend}>
                <FontAwesomeIcon className={styles.buttonIcon} icon={faEnvelope} />
                Enviar por Email
              </Button>
            </div>
          </Col>
        </Row>

        <HiddenableContainer show={showResults && !isLoading}>
          {children}
          <DocumentStatusList
            preventMultipleCurrency={preventMultipleCurrency}
            useToggle={useToggle}
            projectData={projectedData}
            rows={documents}
            onDocumentUpdate={reFetchData}
            onRefreshDataRequired={reFetchData}
            onDocumentToogle={onDocumentToogle}
          />

        </HiddenableContainer>

        <SendDocumentToEmailOffCanvas
          title="Envio de Estado de Cuenta a Email" />

        <LoadingScreen isLoading={isLoading} />
      </>
    )
  }
);

export default ClientStatusForm;